import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";

export const LESSON_REPORTS_PAGE_SIZE = 10;

// Nothing invalidates these queries when a session ends, and the report data
// itself lands minutes later when analysis completes — poll while mounted.
const LESSON_REPORTS_REFETCH_MS = 60_000;

// A lesson whose first session is still running would render an empty report —
// hold its card until a session has actually completed. The server is being
// tightened to leave last_session_completed_at null until then; today it falls
// back to started_at, so this predicate is a no-op until that ships.
export const hasCompletedSession = (card) => Boolean(card.last_session_completed_at);

// The detail endpoint must describe the same window the card list was built
// from, so both query builders share the filter → param mapping.
const setFilterParams = (params, filters) => {
  if (filters.classroomId != null) params.set("classroom_id", filters.classroomId);
  if (filters.lessonId != null) params.set("lesson_id", filters.lessonId);
  if (filters.educatorId) params.set("educator_id", filters.educatorId);
  if (filters.startDate) {
    params.set("start_date", filters.startDate);
    if (filters.endDate) params.set("end_date", filters.endDate);
  } else if (filters.dateRange != null) {
    params.set("date_range", filters.dateRange);
  }
};

const filterKeyOf = (filters) => [
  filters.classroomId ?? null,
  filters.lessonId ?? null,
  filters.dateRange ?? null,
  filters.startDate ?? null,
  filters.endDate ?? null,
  filters.educatorId ?? null,
];

export const useLessonReports = (filters, enabled = true) => {
  const authReady = useAuthReady();

  const { data, error, isInitialLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["lesson-reports", ...filterKeyOf(filters)],
      queryFn: ({ pageParam = 1 }) => {
        const params = new URLSearchParams();
        params.set("page_number", String(pageParam));
        params.set("page_size", String(LESSON_REPORTS_PAGE_SIZE));
        setFilterParams(params, filters);
        return request(`account/lesson_reports?${params}`, { method: "GET" });
      },
      enabled: authReady && enabled,
      refetchInterval: LESSON_REPORTS_REFETCH_MS,
      getNextPageParam: (lastPage, allPages) =>
        (lastPage?.lesson_reports?.length ?? 0) < LESSON_REPORTS_PAGE_SIZE
          ? undefined
          : allPages.length + 1,
    });

  return {
    reports: data ? data.pages.flatMap((page) => page?.lesson_reports ?? []) : [],
    availableLessons: data?.pages?.[0]?.available_lessons ?? [],
    error: error ? error?.detail || error?.message || "Failed to load lesson reports" : null,
    loading: isInitialLoading,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
  };
};

export const useLessonReportDetail = (assignmentId, filters, enabled = true) => {
  const authReady = useAuthReady();

  const { data, error, isInitialLoading } = useQuery({
    queryKey: ["lesson-report-detail", assignmentId, ...filterKeyOf(filters)],
    queryFn: () => {
      const params = new URLSearchParams();
      setFilterParams(params, filters);
      const qs = params.toString();
      return request(`account/lesson_reports/${assignmentId}${qs ? `?${qs}` : ""}`, {
        method: "GET",
      });
    },
    enabled: Boolean(assignmentId) && enabled && authReady,
    refetchInterval: LESSON_REPORTS_REFETCH_MS,
  });

  return {
    detail: data ?? null,
    error: error ? error?.detail || error?.message || "Failed to load lesson report" : null,
    loading: isInitialLoading,
  };
};
